import {
    Alert,
    Button,
    HTMLTable,
    Icon,
    InputGroup
} from '@blueprintjs/core';
import i18n from 'i18next';
import { get } from 'lodash';
import * as React from 'react';
import Footer from '../../components/Footer';
import { ClientTrackingCategories } from '../../models/ClientTrackingCategoryType';
import { ClientTrackingNames } from '../../models/ClientTrackingNameType';
import { ErrorType, ErrorTypes } from '../../models/Errors';
import AuthorisationService, {
    IAuthenticatedDetails,
    IAuthorisationService
} from '../../services/AuthorisationService';
import ConfigurationValuesService, { IConfigurationValuesService } from '../../services/ConfigurationValuesService';
import LoaderService, { ILoaderService } from '../../services/LoaderService';
import { StringFormatService } from '../../services/StringFormatService';
import TrackingService, { ITrackingService } from '../../services/TrackingService';
import TranslationService, { ITranslationService } from '../../services/TranslationService';
import ValidationService from '../../services/ValidationService';
export interface Props {
    candidateId: string;
    documentId: string;
    firstName: string;
    lastName: string;
}

interface ITableDetail {
    detail: string;
    value: string;
}

export interface State {
    emailAddress: string;
    validEmail: boolean;
    emailValidated: boolean;
    tableDetails?: ITableDetail[];
    isModalOpen: boolean;
    errorBody?: string;
    authenticatedDetails?: IAuthenticatedDetails;
    eSigned?: boolean;
    declined?: boolean;
}

class Authorisation extends React.Component<Props, State> {
    private readonly translationService: ITranslationService;
    private readonly authorisationService: IAuthorisationService;
    private readonly configurationValuesService: IConfigurationValuesService;
    private readonly trackingService: ITrackingService;
    private readonly loaderService: ILoaderService;
    private readonly username: string;

    constructor(props: any) {
        super(props);
        this.translationService = TranslationService.getInstance();
        this.authorisationService = new AuthorisationService();
        this.configurationValuesService = ConfigurationValuesService.getInstance();
        this.trackingService = new TrackingService();
        this.loaderService = LoaderService.getInstance();
        this.username = StringFormatService.capitalise(`${props.firstName} ${props.lastName}`);
        i18n.on('languageChanged', () => this.setTableDetails());

        this.state = {
            emailAddress: '',
            validEmail: false,
            emailValidated: false,
            isModalOpen: false
        };
    }

    private translate(key: string) {
        return this.translationService.translate(key);
    }

    private setTableDetails() {
        const getDetail = (key: string) => get(this.state.authenticatedDetails, `translatorData.${key}`);

        const firstName = getDetail('firstName');
        const lastName = getDetail('lastName');
        const address = getDetail('address');
        const date = get(this.state.authenticatedDetails, 'i9Part1SignedOn') || '';

        const tableDetails = [
            {
                detail: this.translate('firstNameLabel'),
                value: StringFormatService.capitalise(firstName ?? '')
            }, {
                detail: this.translate('lastNameLabel'),
                value: StringFormatService.capitalise(lastName ?? '')
            }, {
                detail: this.translate('addressLabel'),
                value: StringFormatService.getAddressString(address!)
            }, {
                detail: this.translate('dateLabel'),
                value: date
            }
        ];
        this.setState({ tableDetails });
    }

    private getErrorString(errorCode?: ErrorType) {
        const key = (errorCode === ErrorTypes.NonMatchingEmail) && 'errorBodyEmail' ||
            (errorCode === ErrorTypes.InvalidStatus || errorCode === ErrorTypes.DocumentExpired) && 'errorBodyModified' ||
            'errorBodyOther';

        return this.translate(key);
    }

    private showErrorModal(errorCode?: ErrorType) {
        const errorBody = this.getErrorString(errorCode);
        this.setState({
            isModalOpen: true,
            errorBody
        });
    }

    private async onEmailSubmitPressAsync() {
        this.loaderService.showLoader();

        const {
            candidateId,
            documentId
        } = this.props;

        const authenticatedDetails: IAuthenticatedDetails = await this.authorisationService.authenticateAsync({
            candidateId,
            documentId,
            translatorEmailAddress: this.state.emailAddress
        });
        this.loaderService.hideLoader();

        if (authenticatedDetails.errorCode) {
            this.trackingService.trackEvent(ClientTrackingCategories.Authorisation, ClientTrackingNames.Error);
            this.showErrorModal(authenticatedDetails.errorCode);
            return;
        }

        this.trackingService.trackEvent(ClientTrackingCategories.Authorisation, ClientTrackingNames.Success);

        this.setState({
            emailValidated: true,
            authenticatedDetails
        }, () => this.setTableDetails());
    }

    private async onDeclinePressAsync() {
            this.loaderService.showLoader();
            const {
                candidateId,
                documentId
            } = this.props;

            const declineDetails = await this.authorisationService.declineAsync({
                candidateId,
                documentId
            });
            this.loaderService.hideLoader();

            if (declineDetails.errorCode) {
                this.trackingService.trackEvent(ClientTrackingCategories.Decline, ClientTrackingNames.Error);
                this.showErrorModal(declineDetails.errorCode);
                return;
            }

            this.trackingService.trackEvent(ClientTrackingCategories.Decline, ClientTrackingNames.Success);
            this.setState({ declined: true });
    }

    private async onSignPressAsync() {
        if (!this.state.authenticatedDetails || !this.state.authenticatedDetails.translatorData) {
            return;
        }

        this.loaderService.showLoader();
        const {
            candidateId,
            documentId
        } = this.props;

        const confirmDetails = await this.authorisationService.confirmAsync({
            candidateId,
            documentId,
            translatorData: this.state.authenticatedDetails.translatorData
        });
        this.loaderService.hideLoader();

        if (confirmDetails.errorCode) {
            this.trackingService.trackEvent(ClientTrackingCategories.Confirm, ClientTrackingNames.Error);
            this.showErrorModal(confirmDetails.errorCode);
            return;
        }

        this.trackingService.trackEvent(ClientTrackingCategories.Confirm, ClientTrackingNames.Success);
        this.setState({ eSigned: true });
    }

    private onEmailChange(emailAddress: string) {
        this.setState({
            emailAddress,
            validEmail: ValidationService.isEmailValid(emailAddress)
        });
    }

    private renderEmailSubmission(emailAddress: string, validEmail: boolean) {
        return (
            <div className='email-submission'>
                <div className='details-text'>
                    {this.translate('emailConfirmText')}
                </div>
                <div className='address-input'>
                    <InputGroup
                        className='email-input'
                        style={{ flex: 1 }}
                        disabled={false}
                        large={true}
                        leftIcon='envelope'
                        onChange={(event: any) => this.onEmailChange(event.target.value)}
                        placeholder={this.translate('emailPlaceholder')}
                        value={emailAddress}
                    />
                    <Button className='submit'
                            disabled={!validEmail}
                            onClick={() => this.onEmailSubmitPressAsync()}>
                        {this.translate('buttonSubmit')}
                    </Button>
                </div>
            </div>
        );
    }

    private renderTable(tableDetails?: ITableDetail[]) {
        return (
            <HTMLTable className='table-details'
                       bordered={true}
                       striped={true}>
                <tbody>
                {
                    tableDetails && tableDetails.map(item => (
                            <tr key={item.detail}>
                                <td className='cell-detail'>{item.detail}</td>
                                <td className='cell-value'>{item.value}</td>
                            </tr>
                        )
                    )
                }
                </tbody>
            </HTMLTable>
        );
    }

    private renderConfirmButtons() {
        return (
            <div className='confirm-buttons'>
                <Button className='decline'
                        onClick={() => this.onDeclinePressAsync()}
                        rightIcon='cross'>
                    {this.translate('buttonDecline')}
                </Button>
                <Button className='confirm'
                        onClick={() => this.onSignPressAsync()}
                        rightIcon='tick'>
                    {this.translate('buttonSign')}
                </Button>
            </div>
        );
    }

    private renderConfirmAction() {
        return (
            <div>
                <div className='confirm-action'>
                    {
                        this.state.eSigned &&
                        <div className='confirm-e-signed'>
                            {this.translate('eSignedLabel')}
                            <Icon icon='tick'
                                  size={20}/>
                        </div>
                    }
                    {
                        this.state.declined &&
                        <div className='confirm-declined'>
                            {this.translate('declinedLabel')}
                            <Icon icon='cross'
                                  size={20}/>
                        </div>
                    }
                </div>
                <div className='thanks-text'>
                    {this.translate('thanksText')}
                </div>
            </div>
        );
    }

    private renderEmailValidated(tableDetails?: ITableDetail[]) {
        const confirmText = StringFormatService.format(
            this.translate('confirmText'),
            [this.username]
        );

        return (
            <div className='email-validated'>
                <div className='details-text'>
                    {this.translate('detailsText')}
                </div>
                {
                    this.renderTable(tableDetails)
                }
                <div className='details-text'>
                    {this.translate('eSignatureText')}
                </div>
                <ul>
                    <li>{this.translate('eSignatureTextItem1')}</li>
                    <li>{this.translate('eSignatureTextItem2')}</li>
                </ul>
                <div className='details-text'>
                    {confirmText}
                </div>
                {
                    (!this.state.eSigned && !this.state.declined) && this.renderConfirmButtons()
                }
                {
                    (this.state.eSigned || this.state.declined) && this.renderConfirmAction()
                }
            </div>
        );
    }

    private renderAlert() {
        return (
            <Alert isOpen={this.state.isModalOpen}
                   className='alert-modal'
                   confirmButtonText={this.translate('okLabel')}
                   onClose={() => this.setState({ isModalOpen: false })}>
                <p>
                    <div className='modal-title modal-error'>
                        {this.translate('errorTitle')}
                    </div>
                    <div className='modal-body'>
                        {this.state.errorBody}
                    </div>
                </p>
            </Alert>
        );
    }

    private renderHeaderImage() {
        if (!this.configurationValuesService.showCustomTranslatorTemplate) {
            return null;
        }
        return (<img className='image'
            src='https://imgs.swipejobs.com/js/JS_phone_hand.jpg' />);
    }

    private renderFooterContent() {
        if (!this.configurationValuesService.showCustomTranslatorTemplate) {
            return null;
        }

        return (<div className='footer-info'>
            <div className='footer-content'>Should you have any questions or issues, please contact our local branch.</div>
            <div className='footer-content'>The PeopleReady | Jobstack Team</div>
            <div className='footer-content link'><a href='https://www.peopleready.com/home'>www.PeopleReady.com</a></div>
            <div className='footer-content address'>1015 A Street | Tacoma, WA 98402</div>
            <div className='footer-image'>
                <img src='https://imgs.swipejobs.com/js/JS+Lockup.jpg' alt='JS_CustomerOnApp_Email2_600x200' width='300' />
            </div>
        </div>);
    }

    public render() {
        const {
            emailAddress,
            validEmail,
            emailValidated,
            tableDetails
        } = this.state;

        const introText = StringFormatService.format(
            this.translate('introText'),
            [this.username]
        );

        return (
            <div className='authorisation'>
                <div className='content'>
                    <div>
                        {this.renderHeaderImage()}
                    </div>
                    <div className='display-linebreak'>
                        {introText}
                    </div>
                    {
                        !emailValidated && this.renderEmailSubmission(emailAddress, validEmail)
                    }
                    {
                        emailValidated && this.renderEmailValidated(tableDetails)
                    }
                    {
                        this.renderAlert()
                    }
                    {
                        this.renderFooterContent()
                    }
                </div>
                <Footer />
            </div>
        );
    }
}

export default Authorisation;



// WEBPACK FOOTER //
// ./src/screens/Authorisation/index.tsx